// client/src/store/chatSessionStore.js

const sessions = new Map();   // canonicalKey -> { state, refs, listeners }
const aliases = new Map();    // oldKey -> canonicalKey (chain-resolved)

export const DRAFT_KEY = '__draft__';

// ─── Alias resolution ──────────────────────────────────────────
// Follows the alias chain until it hits a key with no further alias.
// Guards against accidental circular aliases with a `seen` set.
function resolveKey(key) {
  const seen = new Set();
  while (aliases.has(key) && !seen.has(key)) {
    seen.add(key);
    key = aliases.get(key);
  }
  return key;
}

// ─── Core session lookup (creates bucket if missing) ───────────
// Every public function below routes through this, so alias
// resolution + lazy bucket creation happens in exactly one place.
function getSession(key) {
  const resolved = resolveKey(key);
  if (!sessions.has(resolved)) {
    sessions.set(resolved, { state: {}, refs: {}, listeners: new Set() });
  }
  return sessions.get(resolved);
}

// ─── State (mirrors useState) ───────────────────────────────────
export function getSessionValue(key, field, initialValue) {
  const session = getSession(key);
  if (!(field in session.state)) {
    session.state[field] = typeof initialValue === 'function' ? initialValue() : initialValue;
  }
  return session.state[field];
}

export function setSessionValue(key, field, updater) {
  const session = getSession(key);
  const prev = session.state[field];
  const next = typeof updater === 'function' ? updater(prev) : updater;
  session.state[field] = next;
  session.listeners.forEach((fn) => fn());
}

// ─── Subscription (for useSyncExternalStore) ────────────────────
export function subscribeSession(key, listener) {
  const session = getSession(key);
  session.listeners.add(listener);
  return () => session.listeners.delete(listener);
}

// ─── Refs (mirrors useRef) ───────────────────────────────────────
export function getSessionRef(key, field, initialValue) {
  const session = getSession(key);
  if (!(field in session.refs)) {
    session.refs[field] = { current: typeof initialValue === 'function' ? initialValue() : initialValue };
  }
  return session.refs[field];
}

// ─── Migration: alias oldKey -> newKey WITHOUT deleting oldKey's bucket ──
// Any code still holding a closure over `oldKey` (e.g. handleSend mid-flight)
// will transparently resolve to the same canonical bucket as `newKey`.
// This prevents the "orphaned draft bucket" bug where writes after
// migration created a fresh empty bucket instead of hitting the real one.
export function migrateSessionKey(oldKey, newKey) {
  if (!oldKey || oldKey === newKey) return;

  const resolvedOld = resolveKey(oldKey);
  const resolvedNew = resolveKey(newKey);
  if (resolvedOld === resolvedNew) return; // already aliased together

  if (!sessions.has(resolvedOld)) return; // nothing to migrate

  const session = sessions.get(resolvedOld);

  // Move the bucket to the new key — don't leave a stale sessions entry
  // that clearSession could later wipe, corrupting other chats sharing it.
  sessions.set(resolvedNew, session);
  sessions.delete(resolvedOld);

  // Every future call with oldKey (or anything that used to resolve to it)
  // now resolves straight to resolvedNew.
  aliases.set(resolvedOld, resolvedNew);
  aliases.set(oldKey, resolvedNew);

  session.listeners.forEach((fn) => fn());
}

// ─── Clear a bucket completely (used only for the draft bucket) ──
// Also breaks any alias pointing away from this key, so a fresh
// "new chat" after a migration starts genuinely isolated again.
export function clearSession(key) {
  aliases.delete(key);

  const resolved = resolveKey(key);

  // Only wipe if this is a genuine draft bucket (never migrated to a project).
  // After migrateSessionKey, DRAFT_KEY has no sessions entry — only an alias.
  // Wiping a project bucket would corrupt other chats sharing it via stale references.
  if (resolved !== DRAFT_KEY) return;

  const session = sessions.get(resolved);
  if (session) {
    session.state = {};
    session.refs = {};
    session.listeners.forEach((fn) => fn());
  }
}

// ─── LRU-ish cleanup so long browsing sessions don't leak memory ──
export function pruneOldSessions(activeKeys, max = 12) {
  const resolvedActive = new Set(activeKeys.map(resolveKey));
  const inactive = [...sessions.keys()].filter(
    (k) => !resolvedActive.has(k) && k !== DRAFT_KEY
  );

  while (sessions.size > max && inactive.length) {
    const keyToRemove = inactive.shift();
    sessions.delete(keyToRemove);

    // Clean up any aliases that pointed at the removed bucket
    for (const [aliasKey, target] of aliases.entries()) {
      if (target === keyToRemove) aliases.delete(aliasKey);
    }
  }
}